import { ConfigContents, ServiceType } from '@/types';

const LANGUAGE_OPTIONS_BASE = {
  BA: 'BA',
  BG: 'BG',
  DE: 'DE',
  DK: 'DK',
  EE: 'EE',
  EN: 'EN',
  FI: 'FI',
  FR: 'FR',
  HR: 'HR',
  LT: 'LT',
  LV: 'LV',
  NO: 'NO',
  PL: 'PL',
  RO: 'RO',
  RS: 'RS',
  SE: 'SE',
  SI: 'SI',
  SK: 'SK',
  CY: 'CY', // Fake language to handle wording in GR and content in EN for CY portals
  CZ: 'CZ',
} as const;

// Keep EN first so it remains the default language in testing environments.
const ALL_LANGUAGE_OPTIONS: Array<
  (typeof LANGUAGE_OPTIONS_BASE)[keyof typeof LANGUAGE_OPTIONS_BASE]
> = [
  LANGUAGE_OPTIONS_BASE.EN,
  ...Object.values(LANGUAGE_OPTIONS_BASE).filter(
    (language) => language !== LANGUAGE_OPTIONS_BASE.EN,
  ),
];

export const LANGUAGE_OPTIONS = {
  ...LANGUAGE_OPTIONS_BASE,
  ALL: ALL_LANGUAGE_OPTIONS,
} as const;

export const COMPANY_COUNTRY_OPTIONS = {
  FRANCE: 'FRANCE',
  CYPRUS: 'CYPRUS',
  BOSNIA: 'BOSNIA',
  ERROR: 'Error',
} as const;

export const SERVICE_TYPES = {
  ASTROLOGY: 'astrology',
  FITNESS: 'fitness',
  LOVE: 'love',
  MEDITATION: 'meditation',
  PILATES: 'pilates',
  YOGA: 'yoga',
  COOKING: 'cooking',
} as const satisfies Record<string, string>;

export const FLOW_TYPES = {
  SUB: 'SUB',
  ONE_OFF: 'ONE_OFF',
  FREE: 'FREE',
} as const;

export const WITHDRAWAL_ONLY_HOSTS = new Set([
  'plportal.infinitgame.com',
  'siportal.infinitgame.com',
  'roportal.infinitgame.com',
]);

export const WITHDRAWAL_ONLY_SERVICES_BY_HOST_PREFIX = {
  pl: ['newgamz', 'happystream', 'gymobile'],
  si: ['newgamz', 'happystream', 'gymobile'],
  ro: [
    'newgamz',
    'happystream',
    'gymobile',
    'infinitgame',
    'mystreaming',
    'mygymtrainer',
  ],
} as const;

export type WithdrawalOnlyServiceName =
  (typeof WITHDRAWAL_ONLY_SERVICES_BY_HOST_PREFIX)[keyof typeof WITHDRAWAL_ONLY_SERVICES_BY_HOST_PREFIX][number];

export const SERVICE_NAMES = {
  MyFitGoal: 'MyFitGoal',
  MyPilatesCoach: 'MyPilatesCoach',
  ZenZone: 'ZenZone',
  FitFlowClub: 'FitFlowClub',
  BodyZenMotion: 'BodyZenMotion',
  PilatesHorizon: 'PilatesHorizon',
  Apptitude: 'Apptitude',
  Astrolia: 'Astrolia',
  Lovara: 'Lovara',
  Medisoul: 'Medisoul',
  MyUniverse: 'MyUniverse',
  MyChef: 'MyChef',
  newgamz: 'newgamz', // Withdrawal only
  happystream: 'happystream', // Withdrawal only
  gymobile: 'gymobile', // Withdrawal only
  infinitgame: 'infinitgame', // Withdrawal only
  mystreaming: 'mystreaming', // Withdrawal only
  mygymtrainer: 'mygymtrainer', // Withdrawal only
  Withdrawal: 'Withdrawal', // generic name
  Error: 'Error',
} as const;

export const CONTENT_MEDIA_TYPES = {
  EXERCISES: 'exercises',
  AUDIOS: 'audios',
  VIDEOS: 'videos',
} as const;

export const CONTENT_TEXT_TYPES = {
  RECIPES: 'recipes',
  ARTICLES: 'articles',
  GUIDES: 'guides',
} as const;

export const CONTENT_MEDIA_TAG_ALL = {
  // First letter should be capitalize and match tag from sheets
  PODCAST: 'Podcast',
  MEDITATION: 'Meditation',
};

export const CONTENTS_BY_SERVICE: Record<ServiceType, ConfigContents> = {
  [SERVICE_TYPES.FITNESS]: {
    media: CONTENT_MEDIA_TYPES.EXERCISES,
    text1: CONTENT_TEXT_TYPES.RECIPES,
    text2: CONTENT_TEXT_TYPES.ARTICLES,
  },
  [SERVICE_TYPES.YOGA]: {
    media: CONTENT_MEDIA_TYPES.EXERCISES,
    text1: CONTENT_TEXT_TYPES.RECIPES,
    text2: CONTENT_TEXT_TYPES.ARTICLES,
  },
  [SERVICE_TYPES.PILATES]: {
    media: CONTENT_MEDIA_TYPES.EXERCISES,
    text1: CONTENT_TEXT_TYPES.RECIPES,
    text2: CONTENT_TEXT_TYPES.ARTICLES,
  },
  [SERVICE_TYPES.ASTROLOGY]: {
    media: CONTENT_MEDIA_TYPES.AUDIOS,
    media_multilingual_storage: true,
    text1: null,
    text2: CONTENT_TEXT_TYPES.ARTICLES,
  },
  [SERVICE_TYPES.MEDITATION]: {
    media: CONTENT_MEDIA_TYPES.AUDIOS,
    text1: CONTENT_TEXT_TYPES.GUIDES,
    text2: null,
  },
  [SERVICE_TYPES.LOVE]: {
    media: CONTENT_MEDIA_TYPES.AUDIOS,
    media_multilingual_storage: true,
    text1: null,
    text2: CONTENT_TEXT_TYPES.ARTICLES,
  },
  [SERVICE_TYPES.COOKING]: {
    media: CONTENT_MEDIA_TYPES.VIDEOS,
    text1: CONTENT_TEXT_TYPES.RECIPES,
    text2: CONTENT_TEXT_TYPES.ARTICLES,
  },
};
